import { FC, useEffect, useMemo } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Loader, Svg, Typography, useThemeValue } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getUserProfileCall } from '@/core/state/api';
import { getEmployeesStatsCall } from '@/people/state';
import { Steps } from '../../state';
import { SurveyTemplate } from '../../state/types';

type Props = {
  totalToInvite?: number;
  setStep: (step: Steps) => void;
  setInviteAll: (inviteAll: boolean) => void;
  selectedTemplate?: string;
  preSelection?: boolean;
};

export const StepChooser: FC<Props> = ({
  setStep,
  setInviteAll,
  totalToInvite,
  selectedTemplate,
  preSelection = false,
}) => {
  const t = useTranslations('surveys.sendSurvey.stepChooser');
  const tShared = useTranslations();
  const message = tShared('errors.genericApiError');

  const { response: responseGetUserProfile } = getUserProfileCall.hook();
  const {
    response: responseGetEmployeesStats,
    success: successGetEmployeesStats,
    loading: loadingGetEmployeesStats,
    error: errorGetEmployeesStats,
    execute: executeGetEmployeesStats,
  } = getEmployeesStatsCall.hook();

  const gapXs = useThemeValue('space', 'xs');
  const gapXXS = useThemeValue('space', 'xxs');

  const { genericResponsiveValue } = useResponsiveStyle();

  const name = useMemo(() => {
    if (responseGetUserProfile && responseGetUserProfile.firstName !== undefined) {
      return `${responseGetUserProfile.firstName}`;
    }
    return 'User';
  }, [responseGetUserProfile]);

  const inviteAllTotal = useMemo(
    () =>
      totalToInvite ||
      Number(responseGetEmployeesStats?.total || 0) -
        Number(responseGetEmployeesStats?.completed || 0),
    [totalToInvite, responseGetEmployeesStats],
  );

  const allDescriptionKey = useMemo(() => {
    if (selectedTemplate === SurveyTemplate.HardSkillAssessment) {
      return inviteAllTotal === 1 ? 'all.descriptionHard.single' : 'all.descriptionHard.plural';
    }
    if (selectedTemplate === SurveyTemplate.SoftSkillAssessment) {
      return inviteAllTotal === 1 ? 'all.descriptionSoft.single' : 'all.descriptionSoft.plural';
    }
    return inviteAllTotal === 1 ? 'all.description.single' : 'all.description.plural';
  }, [selectedTemplate, inviteAllTotal]);

  const showAllUserToInviteCount = useMemo(() => {
    return inviteAllTotal > 0;
  }, [inviteAllTotal]);

  useEffect(() => {
    if (!loadingGetEmployeesStats && !successGetEmployeesStats) {
      executeGetEmployeesStats({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]);

  return (
    <Box mb='xs' mt='m' width='100%'>
      <Typography textAlign='center' variant='p2/light'>
        {t('description', {
          name,
        })}
      </Typography>
      {loadingGetEmployeesStats && (
        <Box mt='l' textAlign='center'>
          <Loader size='s' />
        </Box>
      )}
      {errorGetEmployeesStats && (
        <AlertBadge containerProps={{ mt: 'l' }} iconName='info' type='error' label={message} />
      )}
      {successGetEmployeesStats && (
        <Box
          display='grid'
          gridTemplateColumns={genericResponsiveValue({
            breakPoints: { desktop: 'repeat(3, 1fr)' },
            defaultValue: 'repeat(1fr, 1fr)',
          })}
          style={{ gap: gapXs }}
          mt='m'
        >
          <Card
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setStep(Steps.STEP_RECIPIENTS);
              setInviteAll(true);
            }}
            backgroundColor='grey/dark/background'
          >
            <Box display='flex' alignItems='flex-start' flexDirection='row' style={{ gap: gapXXS }}>
              <Svg name='users' size='s' />
              <Typography ml='xxxs' variant='h4'>
                {t('all.title')}
              </Typography>
            </Box>
            <Typography mt='xxs' variant='p2/light'>
              {showAllUserToInviteCount ? t(allDescriptionKey, { total: inviteAllTotal }) : null}
            </Typography>
          </Card>
          <Card
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setStep(Steps.STEP_DEPARTMENTS);
              setInviteAll(false);
            }}
            backgroundColor='grey/dark/background'
          >
            <Box display='flex' alignItems='flex-start' flexDirection='row' style={{ gap: gapXXS }}>
              <Svg name='team' size='s' />
              <Typography ml='xxxs' variant='h4'>
                {t('departments.title')}
              </Typography>
            </Box>
            <Typography mt='xxs' variant='p2/light'>
              {t('departments.description', { total: responseGetEmployeesStats?.completed })}
            </Typography>
          </Card>
          <Card
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setStep(Steps.STEP_EMPLOYEES);
              setInviteAll(false);
            }}
            backgroundColor='grey/dark/background'
          >
            <Box display='flex' alignItems='flex-start' flexDirection='row' style={{ gap: gapXXS }}>
              <Svg name='user' size='s' />
              <Typography ml='xxxs' variant='h4'>
                {t('employer.title')}
              </Typography>
            </Box>
            <Typography mt='xxs' variant='p2/light'>
              {t('employer.description', { total: responseGetEmployeesStats?.completed })}
            </Typography>
          </Card>
        </Box>
      )}
      {preSelection && selectedTemplate !== '' && (
        <Box justifyContent='flex-end' display='flex' flexDirection='row'>
          <Button
            onClick={() => setStep(Steps.STEP_TEMPLATES)}
            mt='l'
            mr='xs'
            variant='link/primary'
          >
            {t('buttons.back')}
          </Button>
        </Box>
      )}
    </Box>
  );
};
